import type { ComponentProps, ReactNode } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { X } from 'lucide-react'
import { cn } from '@/lib/utils'

export const DialogRoot = Dialog.Root
export const DialogTrigger = Dialog.Trigger
export const DialogClose = Dialog.Close
export const DialogTitle = Dialog.Title
export const DialogDescription = Dialog.Description

export function DialogOverlay({ className, ...props }: ComponentProps<typeof Dialog.Overlay>) {
  return (
    <Dialog.Overlay
      className={cn('fixed inset-0 z-50 bg-background/70 backdrop-blur-sm animate-fade-in', className)}
      {...props}
    />
  )
}

/** Centred card dialog for confirmations and small forms. */
export function DialogContent({
  className,
  children,
  ...props
}: ComponentProps<typeof Dialog.Content> & { children: ReactNode }) {
  return (
    <Dialog.Portal>
      <DialogOverlay />
      <Dialog.Content
        className={cn(
          'fixed left-1/2 top-1/2 z-50 w-[calc(100vw-2rem)] max-w-md -translate-x-1/2 -translate-y-1/2',
          'rounded-2xl border bg-popover p-6 text-popover-foreground shadow-soft animate-rise',
          className,
        )}
        {...props}
      >
        {children}
        <Dialog.Close className="absolute right-3 top-3 rounded-lg p-2 text-muted-foreground hover:bg-accent hover:text-foreground">
          <X className="size-4" />
          <span className="sr-only">Close</span>
        </Dialog.Close>
      </Dialog.Content>
    </Dialog.Portal>
  )
}

/** Full-height panel; slides up from the bottom on phones. */
export function SheetContent({
  className,
  children,
  ...props
}: ComponentProps<typeof Dialog.Content> & { children: ReactNode }) {
  return (
    <Dialog.Portal>
      <DialogOverlay />
      <Dialog.Content
        className={cn(
          'fixed inset-x-0 bottom-0 top-0 z-50 flex flex-col bg-background animate-rise sm:inset-x-auto sm:left-1/2 sm:top-20 sm:bottom-auto sm:max-h-[70dvh] sm:w-[36rem] sm:-translate-x-1/2 sm:rounded-2xl sm:border sm:shadow-soft',
          className,
        )}
        {...props}
      >
        {children}
      </Dialog.Content>
    </Dialog.Portal>
  )
}
